import { readonly, ref, watch, type Ref } from 'vue'

import type {
  IChatBackgroundJobStartedEvent,
  IChatContentFilterEvent,
  IChatLlmRetryEvent,
  IChatPartialContextEvent,
  IChatStreamEvent,
  IChatStreamProgressEvent,
  IChatToolCallEvent,
  useEventStream,
} from '@/composables/useEventStream'
import { queryClient } from '@/lib/queryClient'
import { useChatStore } from '@/store/chat'
import { useChatSurveyStore } from '@/store/chatSurvey'
import { useToast } from '@/store/toast'
import { trackBackendRedactions } from '@/utils/chdRedactionTracking'
import { isFileGenerationJobType } from '@/utils/fileGenerationJobs'

import { handleApiErrorToast } from '..'
import { patchChatListTitleInQueryCache } from './chatQueryCacheUtils'
import { adminQueryKeys } from './useAdminQueries'
import {
  CONTENT_FILTER_USER_MESSAGE,
  FE_ERROR_GPT_ACCESS_REVOKED,
  FE_ERROR_GPT_NOT_READY,
  PARTIAL_CONTEXT_USER_MESSAGE,
  STREAM_INTERRUPTED_USER_MESSAGE,
} from '../feErrorCodes'

import type { StreamState } from './chatQueryTypes'

/**
 * Mutable refs plus a readonly {@link StreamState} for UI (streaming flag, error, abort).
 * `streamState.stop` delegates to `stopStream` once the stream registers its abort callback.
 */
export const createStreamState = () => {
  const isStreaming = ref(false)
  const streamError = ref<Error | null>(null)
  const stopStream = ref<(() => void) | null>(null)

  const streamState: StreamState = {
    isStreaming: readonly(isStreaming),
    error: readonly(streamError),
    stop: () => {
      if (stopStream.value) {
        stopStream.value()
      }
    },
  }

  return {
    isStreaming,
    streamError,
    stopStream,
    streamState,
  }
}

/**
 * Wires `eventStream.promise` to clear streaming state; forwards non-abort errors to `reject`.
 * `onComplete` runs after a successful stream end (after `isStreaming` is set false),
 * receiving whether the client aborted the stream (user stop, unmount) rather than the
 * server closing the body — endings this promise cannot otherwise tell apart.
 */
export const setupStreamPromiseHandling = <T = void>(
  eventStream: ReturnType<typeof useEventStream>,
  isStreaming: Ref<boolean>,
  streamError: Ref<Error | null>,

  resolve: (value: T) => void,

  reject: (error: Error) => void,

  onComplete?: (wasAborted: boolean) => void,
) => {
  const stopErrorWatcher = watch(
    () => eventStream.error.value,
    (error) => {
      if (error && error.name !== 'AbortError') {
        streamError.value = error
      }
    },
  )

  eventStream.promise
    .then(() => {
      stopErrorWatcher()
      isStreaming.value = false
      onComplete?.(eventStream.aborted.value)
    })
    .catch((err) => {
      stopErrorWatcher()
      isStreaming.value = false
      const error = err instanceof Error ? err : new Error(String(err))
      // AbortError is not treated as a failure (user intentionally stopped the stream)
      if (error.name === 'AbortError') {
        // Silently resolve instead of rejecting
        return
      }
      streamError.value = error
      reject(error)
    })
}

/** Clears per-mutation stream refs before starting a new SSE request. */
export const resetStreamMutationRefs = (
  isStreaming: Ref<boolean>,
  streamError: Ref<Error | null>,
  stopStream: Ref<(() => void) | null>,
) => {
  isStreaming.value = false
  streamError.value = null
  stopStream.value = null
}

/**
 * TanStack `onSuccess` / `onError` pair for streaming mutations: resets refs and surfaces API errors.
 * Optional chat list invalidation after success (used by {@link newChat}).
 */
export const streamMutationCallbacks = (
  isStreaming: Ref<boolean>,
  streamError: Ref<Error | null>,
  stopStream: Ref<(() => void) | null>,
  options: { invalidateChatsQuery?: boolean } = {},
) => ({
  onSuccess: () => {
    if (options.invalidateChatsQuery) {
      queryClient.invalidateQueries({ queryKey: ['chats'] })
    }
    isStreaming.value = false
    streamError.value = null
    stopStream.value = null
  },
  onError: (error: Error) => {
    isStreaming.value = false
    streamError.value = error
    stopStream.value = null
    if (error instanceof Error && error.name === 'AbortError') {
      return
    }
    const ext = error as Error & {
      streamErrorCode?: string
      streamAffectedChatId?: string
    }
    const streamCode = ext.streamErrorCode
    if (
      streamCode === FE_ERROR_GPT_ACCESS_REVOKED &&
      ext.streamAffectedChatId
    ) {
      const chatStore = useChatStore()
      chatStore.markChatGptAccessRevoked(ext.streamAffectedChatId)
      chatStore.removeLastOptimisticUserMessage(ext.streamAffectedChatId)
      queryClient.invalidateQueries({
        queryKey: ['chats', 'get', ext.streamAffectedChatId],
      })
    }
    if (streamCode === FE_ERROR_GPT_NOT_READY) {
      // Both namespaces: the admin GPT queries live under their own key
      // (see `adminQueryKeys.gpt`), so `['gpts']` alone would miss the admin
      // edit page — where the preview chat raises this very error.
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    }
    handleApiErrorToast(
      error as unknown as Parameters<typeof handleApiErrorToast>[0],
    )
  },
})

/**
 * Advances the typing indicator's label from SSE `stream_progress` (Preparing → Thinking).
 */
export const handleStreamProgress = (payload: IChatStreamProgressEvent) => {
  if (!payload.chat_id) return
  useChatStore().setChatStreamPhase(payload.chat_id, payload.phase)
}

/**
 * Applies a `reasoning_chunk` from the stream. Reasoning precedes the answer, so this also
 * brings the assistant row into being — see `appendAssistantReasoningChunk`.
 */
export const handleReasoningChunk = (chunkData: IChatStreamEvent) => {
  useChatStore().appendAssistantReasoningChunk(chunkData)
}

/**
 * Applies an assistant `message_chunk` from the stream: stops “thinking”, appends streamed text.
 *
 * A `content_replace` chunk leaves “thinking” alone. The API sends those between tool rounds
 * (an HTML page or diagram the moment a tool makes it, or Advanced Mode's narration being
 * dropped), while the model is still working on the rest of the reply.
 * @param setResponding When true, marks the chat as actively responding (new-chat stream path).
 */
export const handleMessageChunk = (
  chunkData: IChatStreamEvent,
  setResponding?: boolean,
) => {
  const chatStore = useChatStore()
  if (chunkData.content_replace === undefined) {
    chatStore.setChatIsThinking(chunkData.chat_id, false)
  }
  chatStore.setChatResponseMessage(chunkData)
  if (setResponding) {
    chatStore.setChatIsResponding(chunkData.chat_id, true)
  }
}

/**
 * Every job the reply started: the one `message_complete` names plus the ones only a
 * `background_job_started` announced. The API names a single job on `message_complete` (an
 * image wins over a deck), so a reply that made a logo and a deck would otherwise lose the deck
 * until a reload.
 */
const collectReplyBackgroundJobs = (
  chunkData: IChatStreamEvent,
  startedJobs: IChatBackgroundJobStartedEvent[],
) => {
  const jobs = startedJobs.map(({ job_type, job_id }) => ({ job_type, job_id }))
  if (
    chunkData.background_job_type &&
    !jobs.some((job) => job.job_type === chunkData.background_job_type)
  ) {
    jobs.push({
      job_type: chunkData.background_job_type,
      job_id: chunkData.job_id ?? '',
    })
  }
  return jobs
}

/**
 * Finalizes one assistant message after `message_complete`: flushes buffer, URLs, mermaid metrics.
 * @param afterFlush Optional hook run after flush (e.g. remove provisional list row, invalidate GPTs).
 * @param startedJobs The stream's `background_job_started` events, so every job the reply
 *   started gets its placeholder rather than only the one `message_complete` names.
 */
export const handleMessageCompleteChunk = (
  chunkData: IChatStreamEvent,
  afterFlush?: () => void,
  startedJobs: IChatBackgroundJobStartedEvent[] = [],
) => {
  const chatStore = useChatStore()
  const replyJobs = collectReplyBackgroundJobs(chunkData, startedJobs)
  const imageJob = replyJobs.find((job) => job.job_type === 'image_generation')

  // Flush streamed content first so merges never overwrite buffered assistant text.
  chatStore.flushAssistantStreamBuffer(chunkData.chat_id)

  const completedMessageId = chunkData.message?.id ?? chunkData.message_id
  if (completedMessageId) {
    chatStore.applyAssistantMessageCompleteFromEvent(chunkData, {
      // The saved content is the final word: the server adds to it outside the text stream
      // (a tool's HTML page or diagram, the link a published site gets) and drops Advanced
      // Mode's narration. Taking it here shows what a reload would, without the reload.
      updateContent: !!imageJob || !!chunkData.message?.content,
      imageJobId: imageJob?.job_id || undefined,
    })

    const completedMessage = chatStore.getChatMessage(
      chunkData.chat_id,
      completedMessageId,
    )
    if (completedMessage) {
      trackBackendRedactions(chunkData.chat_id, [completedMessage])
    }
  }

  if (imageJob) {
    chatStore.clearLatestUserDoGenerateImage(chunkData.chat_id)
  }

  const finishedReplyId = chunkData.message?.id ?? chunkData.message_id
  if (finishedReplyId) {
    void useChatSurveyStore().onReplyCompleted(
      chunkData.chat_id,
      finishedReplyId,
    )
  }

  afterFlush?.()
  chatStore.setChatIsThinking(chunkData.chat_id, false)
  chatStore.setChatIsResponding(chunkData.chat_id, false)
  chatStore.recordAssistantStreamMermaidMetrics(
    chunkData.chat_id,
    chunkData.message_id,
  )
  // newChat sends a nested message object; sendMessage may send only a top-level message_id.
  const fileGenerationMessageId = chunkData.message_id ?? chunkData.message?.id
  const fileJobTypes = replyJobs
    .map((job) => job.job_type)
    .filter(isFileGenerationJobType)
  if (fileJobTypes.length && fileGenerationMessageId) {
    if (!chunkData.message) {
      // sendMessage path: no nested message object was present, so setChatResponseMessage
      // was skipped above. Call it now to ensure the assistant row exists in the store
      // (creates it from message_id if there were no prior message_chunk events).
      chatStore.setChatResponseMessage(chunkData)
    }
    for (const jobType of fileJobTypes) {
      chatStore.markAssistantMessageFileGenerationPending(
        chunkData.chat_id,
        fileGenerationMessageId,
        jobType,
      )
    }
  }
}

/**
 * Stops loaders and tidies partial assistant output when the user aborts or the stream errors.
 * Uses `flushAssistantStreamBufferThenRecordMermaidMetrics` so diagrams get final metrics.
 */
export const stopThinkingAndResponding = (chatId: string) => {
  const chatStore = useChatStore()
  chatStore.flushAssistantStreamBufferThenRecordMermaidMetrics(
    chatId,
    undefined,
  )
  chatStore.clearStreamingProcessingSteps(chatId)
  chatStore.setChatIsThinking(chatId, false)
  chatStore.setChatIsResponding(chatId, false)
  // Stop, stream error and EOF all land here. Without this the optimistic image
  // flag survives them and the user row keeps its placeholder for good — only a
  // successful `message_complete` was clearing it.
  chatStore.clearLatestUserDoGenerateImage(chatId)
}

/**
 * Soft stop: the stream ended with neither `message_complete` nor an `error` event.
 *
 * `handleMessageCompleteChunk` is the only happy-path place that clears `isResponding`,
 * so without this the chat stays pinned in "responding" — stop button stuck, publish
 * toolbar disabled — with no hint that the answer on screen is all there will be.
 * A warning, not an error: the partial text is kept and the chat is still usable.
 */
export const handleInterruptedStream = (chatId: string, message?: string) => {
  stopThinkingAndResponding(chatId)
  useToast().onWarning(message?.trim() || STREAM_INTERRUPTED_USER_MESSAGE)
}

/** Applies `mcp_tool_call_start` / `mcp_tool_call_end`: a step on the reply's steps list. */
export const handleToolCallEvent = (payload: IChatToolCallEvent) => {
  if (!payload.chat_id) return
  useChatStore().recordAssistantToolCall(payload)
}

const isContentFiltered = ({ filters }: IChatContentFilterEvent) =>
  !!(filters?.protected_material?.filtered || filters?.jailbreak?.filtered)

/**
 * Handlers both chat streams share for the events around the answer itself: tool calls, retries
 * and warnings. Build one set per stream — it remembers what it has already warned about.
 *
 * `background_job_started` events are collected into `startedJobs` for the stream's
 * `message_complete` to pass to {@link handleMessageCompleteChunk}: that is where the row gets
 * its id, and where the placeholders are added.
 *
 * Two events are registered only so the choice is explicit rather than a silent skip:
 * - `mcp_tools_loaded`: which connectors were loaded; the tool steps show the ones used.
 * - `image_generation_start`: the image placeholder is already up from the moment of sending.
 */
export const createChatStreamActivityHandlers = (
  startedJobs: IChatBackgroundJobStartedEvent[],
) => {
  let hasWarnedContentFilter = false

  return {
    mcp_tool_call_start: handleToolCallEvent,
    mcp_tool_call_end: handleToolCallEvent,
    llm_retry: (payload: IChatLlmRetryEvent) => {
      if (!payload.chat_id) return
      useChatStore().setChatIsRetrying(payload.chat_id, true)
    },
    content_filter: (payload: IChatContentFilterEvent) => {
      // Sent with every chunk that carries filter results, so warn once.
      if (hasWarnedContentFilter || !isContentFiltered(payload)) return
      hasWarnedContentFilter = true
      useToast().onWarning(CONTENT_FILTER_USER_MESSAGE)
    },
    partial_context: (payload: IChatPartialContextEvent) => {
      useToast().onWarning(
        payload.message?.trim() || PARTIAL_CONTEXT_USER_MESSAGE,
      )
    },
    mcp_tools_loaded: () => {},
    background_job_started: (payload: IChatBackgroundJobStartedEvent) => {
      if (!payload?.job_type) return
      startedJobs.push(payload)
    },
    image_generation_start: () => {},
  }
}

/**
 * Handles `new_title` from POST `/chats` or POST `/chats/:id/messages` streams.
 */
export const handleNewTitleStreamEvent = (titleData: IChatStreamEvent) => {
  if (!titleData.chat_id) return
  const title = titleData.title?.trim() ?? ''
  if (!title) return
  const chatId = titleData.chat_id
  useChatStore().setChatTitle(chatId, title)
  patchChatListTitleInQueryCache(chatId, title)
}
